import cookies from 'js-cookie';
import { ABTest, ABTestEnv, IdentifyType } from '@meitu/ab-test';
import { getAppInfo } from '@meitu/hogger';
import { isPoster, isXiuXiu } from '@meitu/is';
import type { Env as MtStatEnv, InitOptions as MtStatInitOptions, StatsParams } from '@meitu/mtstat-sdk';
import { clientInfo } from '@meitu/pc-bridge';
import { extractUTMParams } from '@meitu/tracer-utm-params';
import { toSnakeDict } from '@meitu/util';
import { getLocalIdentity } from '@meitu/xdesign-identity';
import { getEnterpriseID, getRootDomain, getSuffix } from '@meitu/xdesign-widgets';
import { getJumpQuery } from '@meitu/xdesign-widgets/dist/overseasAccessModal/jumpQuery';
import { isIntl, REACT_APP_ENV, REACT_APP_STAT_ENV } from '@/react-process-env';
import { APP_ID, SDK_MODULE_NAME, SdkModuleName } from '@/types';
import { initABTest } from './ab-test';
import { getAppKey } from './app-key';
import { isMobile } from './common';
import { isDevelopEnv } from './env';
import { getFirstUrl } from './login';
import { getParameterValue, getQuery, getQueryParam } from './query';

/**
 * 埋点信息配置
 */
export type StatConfig = Required<
    Pick<MtStatInitOptions, 'appKey' | 'appLanguage' | 'appVersion' | 'env' | 'moduleId'>
> & { [props: string]: string };

/**
 * 埋点信息配置
 */
export const statConfig: StatConfig = {
    appKey: getAppKey() as string, // 业务方申请的统计appKey，需要用字符串
    env: REACT_APP_STAT_ENV as MtStatEnv,
    moduleId: 'hbsj',
    funcEnter: 'hb',
    abTestKey: REACT_APP_ENV === 'release' ? 'C5F34252F04B6D50' : '2CC0D80138565B20', //如没有，传空即可
    appVersion: process.env.STAT_VERSION || '', // 业务应用的版本号
    appLanguage: 'zh-CN', // 业务应用的语言
};

let statStack: { name: string; options: StatsParams }[] = [];

window._stat_track = (name: string, options: StatsParams) => {
    if (window.mtstat && window.mtstat.isInitialized()) {
        window.mtstat?.track(name, options);
        return;
    }

    statStack.push({ name, options });
};

const getSessionStorage = (key: string) => {
    const value = window.sessionStorage.getItem(key);
    return value && decodeURIComponent(value);
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const setSessionStorage = (key: string, value: any) => {
    window.sessionStorage.setItem(key, encodeURIComponent(value));
};

export const getOperateSource = () => {
    let operateSource = 'web';

    if (isMobile()) {
        operateSource = 'h5';
    } else if (clientInfo.isClient) {
        operateSource = clientInfo.isMac ? 'macos' : 'windows';
    }

    return operateSource;
};

/**
 * @description 获取来源页面的路径
 * @returns
 */
export const getReferrer = () => {
    /**
     * 存在referrer 且存在referrer域名跟当前域名一致 直接返回pathname
     */
    const referrer = document.referrer;
    if (referrer) {
        const { host, pathname } = new URL(referrer);
        if (host === window.location.host) {
            return pathname;
        }
    }
    return '';
};

export const removeUrlParameterFromAddressBar = (param: string) => {
    const url = new URL(window.location.href);
    url.searchParams.delete(param);
    window.history.replaceState(null, '', url.toString());
};

/**
 * @description 获取埋点 source
 * 取值步骤：
 *      1. 从url参数中获取from参数
 *      2. 从sessionStorage中获取from参数
 *      3. 从referrer中获取
 *      4. 默认other
 * @param param
 *
 */
const getSource = (from?: string) => {
    if (from) {
        setSessionStorage('from', from || '');
        removeUrlParameterFromAddressBar('from');
    } else {
        from = getSessionStorage('from') || '';
    }
    return from || getReferrer() || 'other'; // 入口来源
};

export const init = async (serverGid: string, sdkAppId?: string) => {
    const params = getQuery();
    const utm = extractUTMParams();

    const channelValue = (isIntl ? params?.utm_source : params?.channel) || '';

    if (channelValue) {
        setSessionStorage('channel', channelValue);
        setSessionStorage('first_url', decodeURIComponent(params.first_url || '') || window.location.href);
    }

    if (params.matrix_channel) {
        setSessionStorage('matrix_channel', params.matrix_channel || '');
    }

    // 默认先加载本地的 uid
    const uid = window.localStorage.getItem('uid');

    if (!uid) {
        window.mtstat?.logout();
    }

    const { appKey, appVersion, appLanguage, env, moduleId } = statConfig;

    window.mtstat?.init({
        appKey,
        appVersion,
        appLanguage,
        env,
        logLevel: 0, // 调试信息输出控制 0 只输出关键上报日志，1 输出所有的上报日志
        debug: process.env.NODE_ENV === 'development',
        channel:
            params.hb_position || params.hb_operation
                ? 'app'
                : (params.channel && decodeURIComponent(params.channel)) || getSessionStorage('channel') || '',
        moduleId,
    });

    let appId = isIntl ? APP_ID : '';

    if (uid) {
        window.mtstat?.login(uid);
    }

    // 团队id
    const oid = getLocalIdentity()?.oid || '';
    const userMode = getEnterpriseID() ? '1' : '';
    const toolUrl = decodeURIComponent(decodeURIComponent(params.tool_url || ''));
    const jumpQuery = getJumpQuery();

    window.mtstat?.setCommonParams(
        toSnakeDict({
            oid,
            userMode,
            enterpriseId: getEnterpriseID() || '',
            operateSource: getOperateSource(),
            title: document.title,
            url: window.location.href,
            source: getSource(params?.from), // 入口来源
            matrixChannel: params.matrix_channel || getSessionStorage('matrix_channel') || '',
            firstUrl: getFirstUrl(),
            channel:
                params.hb_position || params.hb_operation
                    ? 'app'
                    : decodeURIComponent(params.channel || getSessionStorage('channel') || ''),
            funcEnter: statConfig.funcEnter,
            appId,
            originFunc: params.origin_func ?? '',
            indtoolSource: params.indtool_source || '',
            hbOperation: params.hb_operation || getParameterValue(toolUrl, 'hb_operation') || '',
            hbPosition: params.hb_position || getParameterValue(toolUrl, 'hb_position') || '',
            fromApp: isMobile() && isXiuXiu ? 'mtxx_app' : isMobile() && isPoster ? 'mtsjs_app' : 'browser',
            ...jumpQuery,
        }),
    );

    if (utm?.statsParams != null) {
        window.mtstat?.setCommonStatsParams(utm.statsParams);
    }

    const getClientStatParams = () => {
        const pcGidStoreKey = `_pc_gid${getSuffix()}`;
        const pcAbCodeStoreKey = `_pc_ab_code${getSuffix()}`;
        const pcGid = getQueryParam('pc_gid') || cookies.get(pcGidStoreKey) || '';

        const pcAbCode = getQueryParam('ab_code') || cookies.get(pcAbCodeStoreKey) || '';
        const domain = getRootDomain();
        if (pcGid && getQueryParam('pc_gid')) {
            // 设置域名为主域名 designkit.com
            cookies.set(pcGidStoreKey, pcGid, { domain, expires: 365 });
        }
        if (pcAbCode && getQueryParam('ab_code')) {
            cookies.set(pcAbCodeStoreKey, pcAbCode, { domain, expires: 365 });
        }
        return { pcGid, pcAbCode };
    };
    const { pcGid, pcAbCode } = getClientStatParams();
    if (clientInfo.isClient) {
        window.mtstat?.setGid(serverGid, 'pc');
    } else if (pcGid) {
        window.mtstat?.setCommonParams({
            ab_code: pcAbCode,
        });
        window.mtstat?.setGid(pcGid, 'pc');
        removeUrlParameterFromAddressBar('pc_gid');
        removeUrlParameterFromAddressBar('ab_code');
    } else {
        if (
            params.origin === SdkModuleName.poster ||
            params.origin === SdkModuleName.template ||
            params.origin === SdkModuleName.image
        ) {
            window.mtstat?.setCommonParams(
                toSnakeDict({
                    isSdk: SDK_MODULE_NAME[params.origin] || '',
                    companyKey: params.platform,
                }),
            );
        }
    }

    if (isMobile() && (isPoster || isXiuXiu)) {
        try {
            const [appInfo] = await getAppInfo();
            // 需要拿移动端的gid
            console.log('【appInfo】', appInfo);
            window.mtstat?.setGid(appInfo.gid);
            window.mtstat?.setCommonParams(
                toSnakeDict({
                    gid: appInfo.gid,
                    indtoolSource: getQueryParam('indtool_source') || '',
                }),
            );
        } catch (error) {
            console.error('【getAppInfo】', error);
        }
    }

    // AB Test
    const abCode = params.ab_code || '';

    if (statConfig.abTestKey && clientInfo.isClient && abCode) {
        const abTest = new ABTest({
            appKey: statConfig.abTestKey,
            custom: [{ type: IdentifyType.PcGid, value: serverGid }],
            env: (REACT_APP_ENV === 'release' ? 'release' : 'beta') as ABTestEnv,
        });

        await abTest.init();
        const abInfo = abTest.getABInfo();

        if (abInfo) {
            window.sessionStorage.setItem('AB_TEST_INFO', JSON.stringify(abInfo));
        }

        window.mtstat.setABInfo(abInfo);

        // 是否进入实验api，一定要在setgid之后
        abTest.isInABTesting(+abCode);
    }

    // 由于各个业务实验需求不同，该判断只是为了适配某实验跳转全链路打通

    if (!clientInfo.isClient) {
        // 内嵌PC场景不做实验
        await initABTest();
    }

    window._stat_track = function (name: string, options: StatsParams) {
        if (isDevelopEnv()) {
            console.log('_stat_track =>', name, options);
        } else {
            window.mtstat?.track(name, options);
        }
    };

    if (window.mtstat && statStack.length !== 0) {
        statStack.forEach((item) => {
            window.mtstat?.track(item.name, item.options);
        });

        statStack = [];
    }
};
